import { useCallback, useEffect, useState } from "react";
import type { FeedProductMonitorProduct } from "../api/types";

export const MONITOR_DISPLAY_MAX_PRODUCTS = 3;

type MonitorQueueNeighbor = { id: string; title: string; position: number };

/** อ้างตำแหน่งในคิวจริง แม้ตัวปัจจุบันถูกนำออกจากคิวแล้วแต่ยังแสดงอยู่ */
export function getMonitorQueueNeighbors(
  queue: readonly { id: string; title: string }[],
  activeProductId: string | null | undefined,
  excludedProductIds: readonly string[],
): {
  previous: MonitorQueueNeighbor | null;
  next: MonitorQueueNeighbor | null;
} {
  let previous: MonitorQueueNeighbor | null = null;
  let next: MonitorQueueNeighbor | null = null;
  const index = queue.findIndex((item) => item.id === activeProductId);
  if (index < 0) return { previous, next };
  const excluded = new Set(excludedProductIds);
  for (let i = index - 1; i >= 0; i--) {
    const item = queue[i];
    if (item && !excluded.has(item.id)) {
      previous = { ...item, position: i + 1 };
      break;
    }
  }
  for (let i = index + 1; i < queue.length; i++) {
    const item = queue[i];
    if (item && !excluded.has(item.id)) {
      next = { ...item, position: i + 1 };
      break;
    }
  }
  return { previous, next };
}

const MONITOR_DISPLAY_PRODUCTS_STORAGE_PREFIX =
  "cf-feed-monitor-display-products:v1:";

/** legacy — รวม display + highlight ชุดเก่า */
const LEGACY_COMBINED_STORAGE_PREFIX = "cf-feed-monitor-display-highlight:v1:";

export type MonitorDisplayToggleResult = {
  ok: true;
  replacedProductId?: string;
};

export function monitorDisplayProductsStorageKey(feedId: string): string {
  return `${MONITOR_DISPLAY_PRODUCTS_STORAGE_PREFIX}${feedId}`;
}

function parseLegacyCombinedProductIds(raw: string): string[] {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const productIds: string[] = [];
    for (const item of parsed) {
      if (typeof item !== "string") continue;
      if (item.startsWith("p:")) {
        productIds.push(item.slice(2));
      } else if (item.startsWith("v:")) {
        const rest = item.slice(2);
        const pipeIndex = rest.indexOf("|");
        if (pipeIndex !== -1) {
          productIds.push(rest.slice(pipeIndex + 1));
        }
      } else if (item) {
        productIds.push(item);
      }
    }

    return [...new Set(productIds)].slice(0, MONITOR_DISPLAY_MAX_PRODUCTS);
  } catch {
    return [];
  }
}

function migrateLegacyDisplayProducts(feedId: string): string[] {
  const legacyRaw = localStorage.getItem(
    `${LEGACY_COMBINED_STORAGE_PREFIX}${feedId}`,
  );
  if (!legacyRaw) return [];
  return parseLegacyCombinedProductIds(legacyRaw);
}

export function readMonitorDisplayProductIds(feedId: string): string[] {
  try {
    const storageKey = monitorDisplayProductsStorageKey(feedId);
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((value): value is string => typeof value === "string")
        .slice(0, MONITOR_DISPLAY_MAX_PRODUCTS);
    }

    const migrated = migrateLegacyDisplayProducts(feedId);
    if (migrated.length > 0) {
      writeMonitorDisplayProductIds(feedId, migrated);
    }
    return migrated;
  } catch {
    return [];
  }
}

export function writeMonitorDisplayProductIds(
  feedId: string,
  productIds: string[],
): void {
  try {
    localStorage.setItem(
      monitorDisplayProductsStorageKey(feedId),
      JSON.stringify(productIds.slice(0, MONITOR_DISPLAY_MAX_PRODUCTS)),
    );
  } catch {
    // private browsing / storage disabled
  }
}

export function toggleMonitorDisplayProduct(
  productIds: string[],
  productId: string,
): { productIds: string[]; result: MonitorDisplayToggleResult } {
  if (productIds.includes(productId)) {
    return {
      productIds: productIds.filter((id) => id !== productId),
      result: { ok: true },
    };
  }

  if (productIds.length >= MONITOR_DISPLAY_MAX_PRODUCTS) {
    const [replacedProductId, ...remainingProductIds] = productIds;
    return {
      productIds: [...remainingProductIds, productId],
      result: { ok: true, replacedProductId },
    };
  }

  return {
    productIds: [...productIds, productId],
    result: { ok: true },
  };
}

export function filterProductsForMonitorDisplay(
  products: FeedProductMonitorProduct[],
  productIds: string[],
): FeedProductMonitorProduct[] {
  if (productIds.length === 0) return [];

  return productIds
    .map((productId) => products.find((row) => row.productId === productId))
    .filter((row): row is FeedProductMonitorProduct => row != null);
}

/** sync รายการ product บน pop-out display (localStorage + storage event) */
export function useFeedProductMonitorDisplaySelection(feedId: string) {
  const [displayProductIds, setDisplayProductIds] = useState(() =>
    readMonitorDisplayProductIds(feedId),
  );

  useEffect(() => {
    setDisplayProductIds(readMonitorDisplayProductIds(feedId));
  }, [feedId]);

  useEffect(() => {
    const storageKey = monitorDisplayProductsStorageKey(feedId);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      setDisplayProductIds(readMonitorDisplayProductIds(feedId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [feedId]);

  const toggleProductDisplay = useCallback(
    (productId: string): MonitorDisplayToggleResult => {
      let toggleResult: MonitorDisplayToggleResult = { ok: true };
      setDisplayProductIds((current) => {
        const { productIds, result } = toggleMonitorDisplayProduct(
          current,
          productId,
        );
        toggleResult = result;
        if (!result.ok) return current;
        writeMonitorDisplayProductIds(feedId, productIds);
        return productIds;
      });
      return toggleResult;
    },
    [feedId],
  );

  const filterForDisplay = useCallback(
    (products: FeedProductMonitorProduct[]) =>
      filterProductsForMonitorDisplay(products, displayProductIds),
    [displayProductIds],
  );

  const isProductOnDisplay = useCallback(
    (productId: string) => displayProductIds.includes(productId),
    [displayProductIds],
  );

  const getProductDisplayPosition = useCallback(
    (productId: string) => {
      const index = displayProductIds.indexOf(productId);
      return index === -1 ? null : index + 1;
    },
    [displayProductIds],
  );

  return {
    displayProductIds,
    displayCount: displayProductIds.length,
    maxDisplayProducts: MONITOR_DISPLAY_MAX_PRODUCTS,
    isProductOnDisplay,
    getProductDisplayPosition,
    toggleProductDisplay,
    filterForDisplay,
  };
}
